// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAnalytics, logEvent, Analytics } from "firebase/analytics";
import { getFirestore } from "firebase/firestore";
import { metaPixelService } from './lib/metaPixel';
// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyB5aP7YHsjk89kCwVkto9rsqWf7WOUjktY",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "mocktrail-50ed2.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "mocktrail-50ed2",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "mocktrail-50ed2.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "549023735860",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:549023735860:web:efdbbd5141be1212ee6a61",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "G-72T398EMXZ"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);
const db = getFirestore(app);

// UTM Parameter interface
interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

// Function to extract UTM parameters from URL
export const extractUTMParams = (): UTMParams => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams: UTMParams = {};
  
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
    const value = urlParams.get(param);
    if (value) {
      utmParams[param as keyof UTMParams] = value;
    }
  });
  
  return utmParams;
};

// Function to get stored UTM parameters
export const getStoredUTMParams = (): UTMParams => {
  try {
    const stored = sessionStorage.getItem('utm_params');
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

// Function to store UTM parameters
export const storeUTMParams = (params: UTMParams): void => {
  try {
    if (Object.keys(params).length > 0) {
      sessionStorage.setItem('utm_params', JSON.stringify(params));
    }
  } catch (error) {
    console.error('Failed to store UTM params:', error);
  }
};

// Initialize UTM tracking
export const initializeUTMTracking = (): void => {
  const currentUTM = extractUTMParams();
  if (Object.keys(currentUTM).length > 0) {
    storeUTMParams(currentUTM);
  }
};

// Enhanced analytics helper functions with UTM tracking
export const trackPageView = (pageName: string, additionalParams?: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    logEvent(analytics, 'page_view', {
      page_title: pageName,
      page_location: window.location.href,
      page_path: window.location.pathname,
      ...utmParams,
      ...additionalParams
    });
    console.log(`Page view tracked: ${pageName}`, { utmParams, additionalParams });
  }
};

export const trackSectionView = (sectionName: string, additionalParams?: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    logEvent(analytics, 'section_view', {
      section_name: sectionName,
      page_path: window.location.pathname,
      ...utmParams,
      ...additionalParams
    });
    console.log(`Section view tracked: ${sectionName}`, { utmParams, additionalParams });
  }
};

export const trackButtonClick = (buttonName: string, buttonLocation: string, additionalParams?: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    logEvent(analytics, `button_click_${buttonName}_${buttonLocation}`, {
      button_name: buttonName,
      button_location: buttonLocation,
      page_path: window.location.pathname,
      ...utmParams,
      ...additionalParams
    });
    console.log(`Button click tracked: ${buttonName} in ${buttonLocation}`, { utmParams, additionalParams });
  }
};

export const trackFormSubmission = (formName: string, success: boolean, additionalParams?: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    logEvent(analytics, 'form_submit', {
      form_name: formName,
      success: success,
      page_path: window.location.pathname,
      ...utmParams,
      ...additionalParams
    });
    console.log(`Form submission tracked: ${formName}`, { success, utmParams, additionalParams });
  }
};

export const trackUserInteraction = (interactionType: string, details: string, additionalParams?: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    logEvent(analytics, 'user_interaction', {
      interaction_type: interactionType,
      interaction_details: details,
      page_path: window.location.pathname,
      ...utmParams,
      ...additionalParams
    });
    console.log(`User interaction tracked: ${interactionType} - ${details}`, { utmParams, additionalParams });
  }
};

export const trackCustomEvent = (eventName: string, eventParams: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    logEvent(analytics, eventName, {
      page_path: window.location.pathname,
      ...utmParams,
      ...eventParams
    });
    console.log(`Custom event tracked: ${eventName}`, { utmParams, eventParams });
  }
};

export const trackError = (errorType: string, errorMessage: string, additionalParams?: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    logEvent(analytics, 'error_occurred', {
      error_type: errorType,
      error_message: errorMessage,
      page_path: window.location.pathname,
      ...utmParams,
      ...additionalParams
    });
    console.log(`Error tracked: ${errorType}`, { errorMessage, utmParams, additionalParams });
  }
};

// Microsoft Clarity Integration
export const initializeMicrosoftClarity = (): void => {
  const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
  
  if (!clarityProjectId) {
    console.warn('Microsoft Clarity project ID not found in environment variables');
    return;
  }

  try {
    (function(c: any, l: any, a: any, r: any, i: any, t: any, y: any) {
      c[a] = c[a] || function() { (c[a].q = c[a].q || []).push(arguments) };
      t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", clarityProjectId, null, null);
    
    console.log('Microsoft Clarity initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Microsoft Clarity:', error);
  }
};

// Clarity custom events
export const trackClarityEvent = (eventName: string, details?: Record<string, any>) => {
  try {
    if ((window as any).clarity) {
      (window as any).clarity('event', eventName, details);
      console.log(`Clarity event tracked: ${eventName}`, details);
    }
  } catch (error) {
    console.error('Failed to track Clarity event:', error);
  }
};

// Set Clarity custom tags
export const setClarityTag = (tagName: string, tagValue: string) => {
  try {
    if ((window as any).clarity) {
      (window as any).clarity('set', tagName, tagValue);
      console.log(`Clarity tag set: ${tagName} = ${tagValue}`);
    }
  } catch (error) {
    console.error('Failed to set Clarity tag:', error);
  }
};

export { analytics, db };